import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import {
    Paper,
    RadioGroup,
    Radio,
    FormControlLabel,
    FormHelperText,
} from '@material-ui/core';
import { useIntl } from 'react-intl';
import Button from '../../components/Button/CustomButton';
import styles from '../../assets/jss/pages/productManagementPageStyle';
import CustomTypography from '../../components/Typography/CustomTypography';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import { lavenderTableOrder } from '../../assets/jss/global';
import FormDialog from '../../components/Dialog/FormDialog';
import TableProductPaymentDetail from '../../components/TableProductPaymentDetail/TableProductPaymentDetail';
import CleanableInput from '../../components/Input/CleanableInput';
import SelectorInput from '../../components/Input/SelectorInput';
import Validation from '../../common/Validation';
import InputHasUnitAndCleanable from '../../components/Input/InputHasUnitAndCleanable';
import { ErrorGetToken } from '../../common/ErrorGetToken';
import { platform, PLATFORM } from '../../utils/getPlatform';

const useStyles = makeStyles(styles);

const LivestreamPayment = ({
    openDialogDetail,
    handleCloseDialogDetail,
    registerCheck,
    getRegisterCost,
    eventInfo,
    viewerRegisterLiveStream,
    paymentMethod,
    getTokenError,
    customColor,
    customText,
    locate,
}) => {
    const classes = useStyles(customColor);
    const intl = useIntl();
    const [validateResults, setValidateResults] = useState({});
    const [params, setParams] = useState({
        live_stream_time_ids: '',
        payment_method: 0,
        end_month: +new Date().getMonth() + 1,
        end_year: +new Date().getFullYear(),
    });
    const accountType = { card: '2', credit: '1' };
    const optionMonth = {
        1: 1,
        2: 2,
        3: 3,
        4: 4,
        5: 5,
        6: 6,
        7: 7,
        8: 8,
        9: 9,
        10: 10,
        11: 11,
        12: 12,
    };

    useEffect(() => {
        if (openDialogDetail) {
            // eslint-disable-next-line no-nested-ternary
            const payment = eventInfo.has_bank_payment && eventInfo.has_credit_payment ? 0 : eventInfo.has_bank_payment ? '2' : eventInfo.has_credit_payment ? '1' : 0;
            setParams({
                live_stream_time_ids: '',
                end_month: +new Date().getMonth() + 1,
                end_year: +new Date().getFullYear(),
                payment_method: payment,
            });
        }
    }, [openDialogDetail]);

    const optionYear = () => {
        let year = {};
        const currentYear = new Date().getFullYear();
        // eslint-disable-next-line no-return-assign
        Array.from(Array(20).keys()).forEach((item) => year = { ...year, [currentYear + item]: +currentYear + item });
        return year;
    };

    const handleSetParams = useCallback((field, valueChange) => {
        setParams((prevParams) => ({ ...prevParams, [field]: valueChange }));
    }, []);

    const registerAction = () => (
        <GridContainer
            direction="row"
            justify="center"
            alignItems="center"
        >
            <Button
                color="navy"
                round
                gradient
                className={classes.buttonSmall}
                onClick={() => handlePaymentAndRegister()}
                buttonTextColor={customColor?.button_text_color}
                buttonBackgroundColor={customColor?.button_background_color?.common_button}
            >
                <b>{customText?.button_name?.apply || intl.formatMessage({ id: 'seminar.apply' })}</b>
            </Button>
        </GridContainer>
    );

    const getRegisterId = () => {
        const total = [];
        Object.keys(registerCheck).forEach((id) => {
            if (registerCheck[id][id]) {
                total.push(+id);
            }
        });
        return total;
    };

    const validate = () => {
        const validateRules = {
            payment_method: [
                () => Validation.isNumberNotZero(+params.payment_method),
            ],
        };
        if (params.payment_method === '1') {
            const currentDate = new Date(+new Date().getFullYear(), +new Date().getMonth() + 1);
            const dateExpired = new Date(+params.end_year, +params.end_month);
            validateRules.id_card = [
                () => Validation.isNotEmpty(params.id_card),
                () => Validation.validateLimitNumber(params.id_card, 14),
            ];
            validateRules.user_name = [
                () => Validation.isNotEmpty(params.user_name),
                () => Validation.validateMaxLength(params.user_name, 64),
            ];
            validateRules.security_code = [
                () => Validation.isNotEmpty(params.security_code),
                () => Validation.validateLimitNumber(params.security_code, 3),
            ];
            validateRules.expiration_date = [
                () => Validation.validateCreditExpired(currentDate, dateExpired),
            ];
        }

        const validateResult = Validation.validateAllRules(validateRules);
        if (Object.keys(validateResult).length > 0) {
            setValidateResults(validateResult);
            window.scrollTo(0, 0);
            return false;
        }
        setValidateResults({});
        return true;
    };

    const handlePaymentAndRegister = () => {
        let dataSubmit;
        if (validate() && getRegisterCost) {
            dataSubmit = {
                payment_method: +params.payment_method,
                live_stream_time_ids: getRegisterId(),
            };
            if (params.payment_method === '1') {
                dataSubmit = {
                    ...dataSubmit,
                    card_number: params.id_card,
                    expire_year: +params.end_year % 100,
                    expire_month: +params.end_month,
                    cvc: params.security_code,
                    name: params.user_name,
                };
            }
            handleCloseDialogDetail();
            paymentMethod(+params.payment_method);
            if (+params.payment_method === 2) {
                viewerRegisterLiveStream({
                    data: dataSubmit,
                    language: locate,
                });
            } else if (+params.payment_method === 1) {
                sent(dataSubmit);
            }
        }
    };

    const sent = (value) => {
        // eslint-disable-next-line no-undef
        const paygentToken = new PaygentToken();
        paygentToken.createToken(
            `${process.env.REACT_APP_MERCHANT_ID_GET_TOKEN}`,
            `${process.env.REACT_APP_KEY_GENERATE_GET_TOKEN}`,
            value,
            execPurchase,
        );
    };

    const execPurchase = (response) => {
        if (response.result === '0000') {
            const data = {
                token: response.tokenizedCardObject.token,
                masked_card_number: response.tokenizedCardObject.masked_card_number,
                valid_until: response.tokenizedCardObject.valid_until,
                fingerprint: response.tokenizedCardObject.fingerprint,
                hc: response.hc,
                payment_method: +params.payment_method,
                live_stream_time_ids: getRegisterId(),
            };
            viewerRegisterLiveStream({
                data,
                language: locate,
            });
        } else {
            getTokenError(ErrorGetToken[locate][+response.result]);
        }
    };

    const listLivestreamRegister = () => {
        const listRegister = [];
        Object.keys(registerCheck).forEach((id) => {
            if (registerCheck[id][id]) {
                listRegister.push([registerCheck[id].titleLive, `${intl.formatNumber(registerCheck[id].price)}${intl.formatMessage({ id: 'common.yen' })}`]);
            }
        });
        return [listRegister];
    };

    const handleCloseDialog = () => {
        setParams({
            live_stream_time_ids: '',
            payment_method: 0,
            end_month: +new Date().getMonth() + 1,
            end_year: +new Date().getFullYear(),
        });
        handleCloseDialogDetail();
    };

    return (
        <>
            <FormDialog
                title={intl.formatMessage({ id: 'seminar.paymentProcedure' })}
                titleAlign="center"
                isOpen={openDialogDetail}
                visibleCustomizeCloseBtn
                handleClose={() => handleCloseDialog()}
                actions={registerAction()}
                customColor={customColor?.contents_text_color}
            >
                <GridContainer style={{ paddingTop: '6px', paddingBottom: '6px' }}>
                    <GridItem className={classNames({ [classes.noPadding]: platform !== PLATFORM.OTHER })}>
                        <Paper
                            elevation={3}
                            classes={{
                                root: classNames({
                                    [classes.room]: platform === PLATFORM.OTHER,
                                    [classes.roomMobile]: platform !== PLATFORM.OTHER,
                                }),
                            }}
                        >
                            <GridItem xs={12} md={12} className={classes.titleDetail}>
                                <CustomTypography variant="h6" customColor={customColor?.contents_text_color}>
                                    {intl.formatMessage({ id: 'seminar.applicationDetail' })}
                                </CustomTypography>
                            </GridItem>
                            <GridItem xs={12} md={12} style={{ paddingBottom: '15px' }}>
                                <TableProductPaymentDetail
                                    cellBackgroundColor={customColor?.shop_table?.record_background_color}
                                    cellTextColorHear={customColor?.contents_text_color}
                                    cellTextColorBody={customColor?.contents_text_color}
                                    colorRowTitle={customColor?.shop_table?.title_background_color || lavenderTableOrder}
                                    onClick={() => {}}
                                    tableHeadWidth={['75%', '25%']}
                                    tableTitleRow={[[eventInfo?.settings_texts_sidebar_viewer?.field_name?.livestreaming
                                        || intl.formatMessage({ id: 'userRouters.liveStream' })]]}
                                    tableData={listLivestreamRegister()}
                                    hover={false}
                                    pointerLess
                                    className={classes.customTable}
                                    lengthCol={2}
                                />
                            </GridItem>
                            <GridItem xs={12} md={12} className={classes.titlePrice}>
                                <CustomTypography variant="h6" bold customColor={customColor?.contents_text_color}>
                                    {`${intl.formatMessage({ id: 'seminar.totalAmount' })} : ${
                                        intl.formatNumber(getRegisterCost)}${intl.formatMessage({ id: 'common.yen' })} `}
                                    <span style={{ fontSize: '14px' }}>{intl.formatMessage({ id: 'seminar.taxInclude' })}</span>
                                </CustomTypography>
                            </GridItem>
                        </Paper>
                    </GridItem>
                    <GridItem className={classNames({ [classes.payment]: platform === PLATFORM.OTHER, [classes.paymentMarginTop]: platform !== PLATFORM.OTHER })}>
                        <CustomTypography bold variant="h6" customColor={customColor?.contents_text_color}>
                            <span style={{ color: 'red' }}>※</span>
                            {intl.formatMessage({ id: 'common.selectPayment' })}
                        </CustomTypography>
                        <GridItem className={classNames({ [classes.noPadding]: platform !== PLATFORM.OTHER })}>
                            { !eventInfo.has_credit_payment && !eventInfo.has_bank_payment && (
                                <CustomTypography variant="h7" customColor={customColor?.contents_text_color}>
                                    {intl.formatMessage({ id: 'common.noPaymentMethod' })}
                                </CustomTypography>
                            )}
                            <RadioGroup
                                aria-label="bank_account_type"
                                name="bank_account_type"
                                value={params.payment_method}
                                onChange={(e) => setParams({
                                    ...params,
                                    payment_method: e.target.value,
                                })}
                                row
                            >
                                {eventInfo.has_credit_payment && (
                                    <FormControlLabel
                                        value={accountType.credit}
                                        control={<Radio color="primary" checked={params.payment_method === '1'} />}
                                        label={<CustomTypography customColor="#4b4b4b">{intl.formatMessage({ id: 'common.credit' })}</CustomTypography>}
                                        className={classes.colorIconRadio}
                                    />
                                )}
                                {eventInfo.has_bank_payment && (
                                    <FormControlLabel
                                        value={accountType.card}
                                        control={<Radio color="primary" checked={params.payment_method === '2'} />}
                                        label={<CustomTypography customColor="#4b4b4b">{intl.formatMessage({ id: 'common.bankTransfer' })}</CustomTypography>}
                                        className={eventInfo.has_credit_payment && classes.radioButton}
                                    />
                                )}
                            </RadioGroup>
                            <FormHelperText error className="MuiFormHelperText-contained">
                                {validateResults.payment_method
                                    ? intl.formatMessage({ id: validateResults.payment_method.message }) : ''}
                            </FormHelperText>
                            {params.payment_method === '1' && (
                                <GridItem className={classNames({ [classes.paperInfoOrder]: true, [classes.noPaddingOrder]: platform !== PLATFORM.OTHER })}>
                                    <GridContainer style={{ paddingBottom: '15px' }}>
                                        <GridItem xs={12} md={3} className={classes.leftTitle}>
                                            <CustomTypography required customColor={customColor?.contents_text_color}>
                                                {intl.formatMessage({ id: 'common.cardNumber' })}
                                            </CustomTypography>
                                        </GridItem>
                                        <GridItem xs={12} md={8} className={classes.rightInput}>
                                            <InputHasUnitAndCleanable
                                                classes={{ root: classes.colorTextInput }}
                                                defaultValue={params.id_card}
                                                maxLength={16}
                                                onChange={(value) => handleSetParams('id_card', value)}
                                                error={!!validateResults.id_card}
                                                helperText={validateResults.id_card
                                                    ? intl.formatMessage({ id: validateResults.id_card.message }, { minNumber: 14, maxNumber: 16 }) : ''}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                    <GridContainer>
                                        <GridItem xs={12} md={3} className={classes.leftTitle}>
                                            <CustomTypography required customColor={customColor?.contents_text_color}>
                                                {intl.formatMessage({ id: 'common.dateOfExpiry' })}
                                            </CustomTypography>
                                        </GridItem>
                                        <GridItem xs={12} md={8} className={!validateResults.expiration_date ? classes.rightSelect : classes.rightSelectNoMargin}>
                                            <SelectorInput
                                                isCustomColor
                                                valueSelected={+params.end_month}
                                                optionList={optionMonth}
                                                onChange={(valueInput) => handleSetParams('end_month', optionMonth[valueInput])}
                                                error={!!validateResults.expiration_date}
                                                customColor={customColor?.contents_text_color}
                                            />
                                            <span className={classes.titleMonth}>{intl.formatMessage({ id: 'common.month' })}</span>
                                            <SelectorInput
                                                isCustomColor
                                                valueSelected={+params.end_year}
                                                optionList={optionYear()}
                                                onChange={(valueInput) => handleSetParams('end_year', optionYear()[valueInput])}
                                                error={!!validateResults.expiration_date}
                                                customColor={customColor?.contents_text_color}
                                            />
                                            <span className={classes.titleYear}>{intl.formatMessage({ id: 'common.year' })}</span>
                                        </GridItem>
                                    </GridContainer>
                                    {!!validateResults.expiration_date && (
                                        <GridContainer>
                                            <GridItem xs={12} md={3} className={classes.leftTitle} />
                                            <GridItem xs={12} md={8} className={classes.rightInput}>
                                                <FormHelperText error className="MuiFormHelperText-contained">
                                                    {validateResults.expiration_date
                                                        ? intl.formatMessage({ id: validateResults.expiration_date.message }) : ''}
                                                </FormHelperText>
                                            </GridItem>
                                        </GridContainer>
                                    )}
                                    <GridContainer>
                                        <GridItem xs={12} md={3} className={classes.leftTitle}>
                                            <CustomTypography required customColor={customColor?.contents_text_color}>
                                                {intl.formatMessage({ id: 'common.nominalName' })}
                                            </CustomTypography>
                                        </GridItem>
                                        <GridItem xs={12} md={8} className={classes.rightInput}>
                                            <CleanableInput
                                                classes={{ root: classes.colorTextInput }}
                                                className={classes.right}
                                                defaultValue={params.user_name}
                                                onChange={(value) => handleSetParams('user_name', value)}
                                                error={!!validateResults.user_name}
                                                helperText={validateResults.user_name
                                                    ? intl.formatMessage({ id: validateResults.user_name.message }, { maxLength: 64 }) : ''}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                    <GridContainer>
                                        <GridItem xs={12} md={3} className={classes.leftTitle}>
                                            <CustomTypography required customColor={customColor?.contents_text_color}>
                                                {intl.formatMessage({ id: 'common.securityCode' })}
                                            </CustomTypography>
                                        </GridItem>
                                        <GridItem xs={12} md={8} className={classes.rightInput}>
                                            <InputHasUnitAndCleanable
                                                classes={{ root: classes.colorTextInput }}
                                                defaultValue={params.security_code}
                                                maxLength={4}
                                                onChange={(value) => handleSetParams('security_code', value)}
                                                error={!!validateResults.security_code}
                                                helperText={validateResults.security_code
                                                    ? intl.formatMessage({ id: validateResults.security_code.message }, { minNumber: 3, maxNumber: 4 }) : ''}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>
                            )}
                        </GridItem>
                    </GridItem>
                </GridContainer>
            </FormDialog>
        </>
    );
};

LivestreamPayment.defaultProps = {
    openDialogDetail: false,
    handleCloseDialogDetail: () => {},
    viewerRegisterLiveStream: () => {},
    registerCheck: {},
    getRegisterCost: 0,
    eventInfo: {},
    paymentMethod: {},
    getTokenError: () => {},
    customText: {},
    customColor: {},
    locate: 'ja',
};

LivestreamPayment.propTypes = {
    openDialogDetail: PropTypes.bool,
    handleCloseDialogDetail: PropTypes.func,
    viewerRegisterLiveStream: PropTypes.func,
    registerCheck: PropTypes.oneOfType([() => null, PropTypes.object]),
    getRegisterCost: PropTypes.number,
    eventInfo: PropTypes.oneOfType([() => null, PropTypes.object]),
    paymentMethod: PropTypes.func,
    getTokenError: PropTypes.func,
    customText: PropTypes.oneOfType([() => null, PropTypes.object]),
    customColor: PropTypes.oneOfType([() => null, PropTypes.object]),
    locate: PropTypes.string,
};

export default LivestreamPayment;
